/**
 * Product Hub — the twelve warnings of doc 2 §50.
 *
 * These are what the product summary shows before somebody activates a product, and every one of them
 * describes a state the database **permits on purpose**: a product is assembled over several sittings,
 * and a schema that refused half-finished work would make the wizard impossible. So the rule of this
 * file is the same as the inventory anomaly list's — flag, never block.
 *
 * Its own file rather than a section of `productHub.catalog.ts` because the inputs come from six
 * modules at once (catalog, pricing, inventory, media, bundles, fulfillment). It is the one pure
 * function in the module that is deliberately *about* the whole of it, which is also why it takes a
 * flattened summary rather than the aggregates themselves: a resolver that needed a `Product` model
 * would drag Sequelize into `libs/types`.
 *
 * No React, no MUI, no browser globals — the backend imports this file directly through
 * `@malash-office/types/product-hub/product-warnings`.
 */
import { PhOption, phOptions } from './productHub.core';

/** The codes, in the order §50 lists them. */
export const PRODUCT_WARNINGS = [
  'missing_sku',
  'duplicate_sku',
  'missing_price',
  'store_currency_requires_store_price',
  'missing_media',
  'missing_warehouse',
  'store_missing_fulfillment_path',
  'bundle_missing_components',
  'bundle_component_is_bundle',
  'no_delivery_option',
  'invalid_allocation',
  'missing_package_dimensions'
] as const;
export type ProductWarningCode = (typeof PRODUCT_WARNINGS)[number];

export const PRODUCT_WARNING_LABELS: Record<ProductWarningCode, string> = {
  missing_sku: 'וריאציה ללא מק״ט',
  duplicate_sku: 'מק״ט כפול',
  missing_price: 'וריאציה ללא מחיר בסיס',
  store_currency_requires_store_price: 'חנות במטבע אחר דורשת מחיר ייעודי',
  missing_media: 'למוצר אין תמונות',
  missing_warehouse: 'אין מחסן זכאי למוצר',
  store_missing_fulfillment_path: 'חנות ללא דרך אספקה למוצר',
  bundle_missing_components: 'מארז ללא רכיבים',
  bundle_component_is_bundle: 'רכיב מארז הוא עצמו מארז',
  no_delivery_option: 'למוצר אין דרך אספקה',
  invalid_allocation: 'הקצאה גבוהה מהזמינות',
  missing_package_dimensions: 'חסרות מידות אריזה'
};

export const PRODUCT_WARNING_OPTIONS: PhOption<ProductWarningCode>[] = phOptions(
  PRODUCT_WARNINGS,
  PRODUCT_WARNING_LABELS
);

/**
 * How loudly to draw each one.
 *
 * `error` is reserved for the three that make the product **unsellable as configured** — a duplicate
 * SKU, a bundle with no components, a bundle inside a bundle. Everything else is work still to do,
 * and colouring it red would train people to ignore red.
 */
export const PRODUCT_WARNING_SEVERITY: Record<ProductWarningCode, 'error' | 'warning'> = {
  missing_sku: 'error',
  duplicate_sku: 'error',
  bundle_missing_components: 'error',
  bundle_component_is_bundle: 'error',
  missing_price: 'warning',
  store_currency_requires_store_price: 'warning',
  missing_media: 'warning',
  missing_warehouse: 'warning',
  store_missing_fulfillment_path: 'warning',
  no_delivery_option: 'warning',
  invalid_allocation: 'warning',
  missing_package_dimensions: 'warning'
};

/**
 * Which step of the wizard fixes it.
 *
 * The summary's "עריכה" links use this, and it is a map rather than a `switch` in the component so the
 * wizard and the product card agree about where a given problem is repaired.
 */
export const PRODUCT_WARNING_STEP: Record<ProductWarningCode, 0 | 1> = {
  missing_sku: 0,
  duplicate_sku: 0,
  missing_price: 0,
  missing_media: 0,
  bundle_missing_components: 0,
  bundle_component_is_bundle: 0,
  no_delivery_option: 0,
  missing_package_dimensions: 0,
  // The four that are about stores are fixed in the assignment step.
  store_currency_requires_store_price: 1,
  missing_warehouse: 1,
  store_missing_fulfillment_path: 1,
  invalid_allocation: 1
};

/* ------------------------------------------------------------------ */
/* The input                                                           */
/* ------------------------------------------------------------------ */

/** One variant, as the check needs it. */
export interface WarningVariantInput {
  variantId?: number;
  name: string;
  sku: string;
  basePrice: number | null;
  baseCurrency: string;
  /** Whether **any** warehouse is eligible for it. Resolved by the caller through ADR-009's ladder. */
  hasEligibleWarehouse?: boolean;
  packageWeight?: number | null;
  packageLength?: number | null;
  packageWidth?: number | null;
  packageHeight?: number | null;
}

/** One store the product is assigned to, with the facts the store-side warnings need. */
export interface WarningStoreInput {
  storeId: number;
  name: string;
  currency: string;
  /** Variant ids that have a `StorePrice` in this store. */
  pricedVariantIds: number[];
  /** False when the fulfillment resolver found no method for this store (§47). */
  hasFulfillmentPath?: boolean;
  /** Allocations above what is physically available, from the inventory resolver. */
  invalidAllocationVariantIds?: number[];
}

export interface ProductWarningInput {
  productType: string;
  variantStructure: string;
  canShip: boolean;
  canPickup: boolean;
  mediaCount: number;
  variants: WarningVariantInput[];
  stores: WarningStoreInput[];
  /** Bundle components, when the product is a bundle. `componentIsBundle` is resolved by the caller. */
  bundleComponents?: { componentVariantId: number; quantity: number; componentIsBundle: boolean }[];
  /** SKUs that exist elsewhere in the catalogue — the caller queries, this file compares. */
  duplicateSkus?: string[];
}

export interface ProductWarning {
  code: ProductWarningCode;
  label: string;
  severity: 'error' | 'warning';
  /** Which wizard step repairs it. */
  step: 0 | 1;
  /** The specific row it is about, so the UI can point at it rather than at the product. */
  detail: string;
  variantId?: number;
  storeId?: number;
}

const warning = (code: ProductWarningCode, detail: string, ids: Partial<ProductWarning> = {}): ProductWarning => ({
  code,
  label: PRODUCT_WARNING_LABELS[code],
  severity: PRODUCT_WARNING_SEVERITY[code],
  step: PRODUCT_WARNING_STEP[code],
  detail,
  ...ids
});

/** Does this variant have enough of its package box to be shipped by a carrier that charges by size? */
const hasPackageDimensions = (variant: WarningVariantInput): boolean =>
  !!variant.packageWeight && !!variant.packageLength && !!variant.packageWidth && !!variant.packageHeight;

/**
 * Every warning that applies, errors first.
 *
 * The order within a severity is the order of §50, so two people reading the same summary read it the
 * same way. Nothing here throws and nothing is deduplicated across variants: "three variants have no
 * price" is three rows, because the fix is three edits.
 */
export const productWarnings = (input: ProductWarningInput): ProductWarning[] => {
  const found: ProductWarning[] = [];
  const isBundle = input.productType === 'bundle';
  const isPhysical = input.productType === 'physical';

  input.variants.forEach(variant => {
    const label = variant.name || variant.sku || 'וריאציה';

    if (!variant.sku?.trim()) found.push(warning('missing_sku', label, { variantId: variant.variantId }));
    if (variant.sku && (input.duplicateSkus ?? []).includes(variant.sku)) {
      found.push(warning('duplicate_sku', `${label} — ${variant.sku}`, { variantId: variant.variantId }));
    }
    if (variant.basePrice === null) found.push(warning('missing_price', label, { variantId: variant.variantId }));

    // Only asked of physical products, and only when it is knowable: a digital product has no box, and
    // `hasEligibleWarehouse === undefined` means the caller did not resolve the ladder.
    if (isPhysical && variant.hasEligibleWarehouse === false) {
      found.push(warning('missing_warehouse', label, { variantId: variant.variantId }));
    }
    if (isPhysical && input.canShip && !hasPackageDimensions(variant)) {
      found.push(warning('missing_package_dimensions', label, { variantId: variant.variantId }));
    }
  });

  if (isPhysical && input.mediaCount === 0) found.push(warning('missing_media', 'לא הועלו תמונות למוצר'));

  // §47: neither shipping nor pickup is a legitimate half-configured state, and the summary is exactly
  // where it should be caught.
  if (!input.canShip && !input.canPickup) {
    found.push(warning('no_delivery_option', 'המוצר אינו ניתן למשלוח ואינו לאיסוף עצמי'));
  }

  if (isBundle) {
    const components = input.bundleComponents ?? [];
    if (!components.length) found.push(warning('bundle_missing_components', 'למארז לא הוגדרו רכיבים'));

    components
      .filter(component => component.componentIsBundle)
      .forEach(component =>
        // ADR-015: a bundle inside a bundle has no defined availability, so this is an error rather
        // than a nudge.
        found.push(warning('bundle_component_is_bundle', `רכיב ${component.componentVariantId} הוא מארז`))
      );
  }

  input.stores.forEach(store => {
    // §17-18 and ADR-003: a base price is inherited only in the store's own currency, so a store in a
    // different one needs its own price for every variant.
    const foreignCurrency = input.variants.filter(variant => variant.baseCurrency !== store.currency);
    const unpriced = foreignCurrency.filter(
      variant => variant.variantId === undefined || !store.pricedVariantIds.includes(variant.variantId)
    );

    if (unpriced.length) {
      found.push(
        warning(
          'store_currency_requires_store_price',
          `${store.name} (${store.currency}) — ${unpriced.length} וריאציות ללא מחיר ייעודי`,
          { storeId: store.storeId }
        )
      );
    }

    if (store.hasFulfillmentPath === false) {
      found.push(warning('store_missing_fulfillment_path', store.name, { storeId: store.storeId }));
    }

    (store.invalidAllocationVariantIds ?? []).forEach(variantId =>
      found.push(
        warning('invalid_allocation', `${store.name} — וריאציה ${variantId}`, { storeId: store.storeId, variantId })
      )
    );
  });

  return found.sort((a, b) => {
    if (a.severity !== b.severity) return a.severity === 'error' ? -1 : 1;
    return PRODUCT_WARNINGS.indexOf(a.code) - PRODUCT_WARNINGS.indexOf(b.code);
  });
};
